/**
 * Task Tracking Helper
 * Functions to track task-related actions in Heap Analytics
 *
 * Architecture Note:
 * These functions do NOT check if Heap is available before calling trackEvent.
 * This is intentional - the underlying trackEvent function in heapAnalytics.js
 * handles all availability checks, error handling, and graceful degradation.
 *
 * Benefits of this approach:
 * - Single source of truth for Heap availability checks
 * - Consistent error handling across all tracking functions
 * - Easier to maintain and test
 * - Prevents code duplication
 *
 * The trackEvent function will:
 * - Check if Heap is loaded (window.heap exists)
 * - Log warnings in development if Heap is unavailable
 * - Gracefully fail without throwing errors
 * - Handle all exceptions with try-catch
 */

import { trackEvent } from './heapAnalytics';
import { AnalyticsEventNames, TaskStatus, TaskAction } from './constants';

/**
 * Track task creation
 * @param {Object} taskData - Task data
 * @param {string} taskData.taskId - Task ID
 * @param {string} taskData.taskTitle - Task title
 * @param {string} taskData.taskStatus - Task status
 * @param {string} taskData.assignedTo - User ID of assigned user
 */
export const trackTaskCreated = taskData => {
  try {
    const eventData = {
      taskId: taskData.taskId || '',
      taskTitle: taskData.taskTitle || '',
      taskStatus: taskData.taskStatus || TaskStatus.TODO,
      assignedTo: taskData.assignedTo || '',
      taskAction: TaskAction.CREATE,
    };

    trackEvent(AnalyticsEventNames.TASK_CREATED, eventData);
  } catch (error) {
    console.error('[Heap] Error tracking task creation:', error);
  }
};

/**
 * Track task edit/modification
 * @param {Object} taskData - Task data
 * @param {string} taskData.taskId - Task ID
 * @param {string} taskData.taskTitle - Task title
 * @param {string} taskData.taskStatus - Task status
 */
export const trackTaskEdited = taskData => {
  try {
    const eventData = {
      taskId: taskData.taskId || '',
      taskTitle: taskData.taskTitle || '',
      taskStatus: taskData.taskStatus || '',
      taskAction: TaskAction.EDIT,
    };

    trackEvent(AnalyticsEventNames.TASK_EDITED, eventData);
  } catch (error) {
    console.error('[Heap] Error tracking task edit:', error);
  }
};

/**
 * Track task deletion
 * @param {Object} taskData - Task data
 * @param {string} taskData.taskId - Task ID
 * @param {string} taskData.taskTitle - Task title
 */
export const trackTaskDeleted = taskData => {
  try {
    const eventData = {
      taskId: taskData.taskId || '',
      taskTitle: taskData.taskTitle || '',
      taskAction: TaskAction.DELETE,
    };

    trackEvent(AnalyticsEventNames.TASK_DELETED, eventData);
  } catch (error) {
    console.error('[Heap] Error tracking task deletion:', error);
  }
};

/**
 * Normalize status value to match backend format (lowercase with hyphens)
 * Handles various status formats: 'In Progress', 'in_progress', 'in-progress', etc.
 * @param {string} status - Status value to normalize
 * @returns {string} Normalized status value
 */
const normalizeStatus = status => {
  if (!status) return '';
  return status
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/_/g, '-'); // Replace underscores with hyphens
};

/**
 * Track task status change
 * @param {Object} taskData - Task data
 * @param {string} taskData.taskId - Task ID
 * @param {string} taskData.taskTitle - Task title
 * @param {string} taskData.oldStatus - Previous status
 * @param {string} taskData.newStatus - New status
 */
export const trackTaskStatusChanged = taskData => {
  try {
    // Normalize status values for consistent tracking
    const normalizedNewStatus = normalizeStatus(taskData.newStatus);
    const normalizedOldStatus = normalizeStatus(taskData.oldStatus);

    const eventData = {
      taskId: taskData.taskId || '',
      taskTitle: taskData.taskTitle || '',
      oldStatus: normalizedOldStatus,
      newStatus: normalizedNewStatus,
      taskAction: TaskAction.STATUS_CHANGE,
    };

    trackEvent(AnalyticsEventNames.TASK_STATUS_CHANGED, eventData);
  } catch (error) {
    console.error('[Heap] Error tracking task status change:', error);
  }
};

/**
 * Track task comment
 * @param {Object} taskData - Task data
 * @param {string} taskData.taskId - Task ID
 * @param {string} taskData.taskTitle - Task title
 * @param {string} taskData.comment - Comment text
 */
export const trackTaskCommented = taskData => {
  try {
    const eventData = {
      taskId: taskData.taskId || '',
      taskTitle: taskData.taskTitle || '',
      hasComment: !!taskData.comment,
      taskAction: TaskAction.COMMENT,
    };

    trackEvent(AnalyticsEventNames.TASK_COMMENTED, eventData);
  } catch (error) {
    console.error('[Heap] Error tracking task comment:', error);
  }
};

/**
 * Track task feedback submission
 *
 * This function tracks when users provide feedback on tasks (thumbs up/down and comments).
 * It should be called when feedback is submitted via CompleteTaskModal or FeedbackBlock.
 *
 * @param {Object} taskData - Task data
 * @param {string} taskData.taskId - Task ID
 * @param {string} taskData.taskTitle - Task title
 * @param {string} taskData.taskStatus - Current task status
 * @param {number} taskData.feedbackSentiment - Feedback sentiment (1=positive, -1=negative, 0=none)
 * @param {string} [taskData.feedbackComments] - Optional feedback comments
 */
export const trackTaskFeedback = taskData => {
  try {
    // Map numeric sentiment to readable string
    let sentimentLabel = 'none';
    if (taskData.feedbackSentiment === 1) {
      sentimentLabel = 'positive';
    } else if (taskData.feedbackSentiment === -1) {
      sentimentLabel = 'negative';
    }

    const eventData = {
      taskId: taskData.taskId || '',
      taskTitle: taskData.taskTitle || '',
      taskStatus: taskData.taskStatus || '',
      feedbackSentiment: sentimentLabel,
      hasFeedbackComments: !!taskData.feedbackComments,
      feedbackLength: taskData.feedbackComments?.length || 0,
    };

    trackEvent(AnalyticsEventNames.TASK_FEEDBACK_SUBMITTED, eventData);
  } catch (error) {
    console.error('[Heap] Error tracking task feedback:', error);
  }
};

/**
 * Track task view/open
 * @param {Object} taskData - Task data
 * @param {string} taskData.taskId - Task ID
 * @param {string} taskData.taskTitle - Task title
 * @param {string} taskData.taskStatus - Task status
 * @param {string} taskData.taskType - Task type (summary, error, improvement, general)
 */
export const trackTaskViewed = taskData => {
  try {
    const eventData = {
      taskId: taskData.taskId || '',
      taskTitle: taskData.taskTitle || '',
      taskStatus: taskData.taskStatus || '',
      taskType: taskData.taskType || '',
    };

    trackEvent(AnalyticsEventNames.TASK_VIEWED, eventData);
  } catch (error) {
    console.error('[Heap] Error tracking task view:', error);
  }
};
